import React, { useCallback, useMemo } from 'react'

import { DefaultRawDatum } from '@nivo/pie'
import { createColumnHelper } from '@tanstack/react-table'
import { HStack, T, Table, VStack } from 'govwell-ui'
import { twMerge } from 'tailwind-merge'
import { GeneralLedgerNumberFragment, TimeZone } from 'types/graphql'

import { PieChart } from 'src/components/Visualizations/PieChart/PieChart'
import { getLedgerColor } from 'src/utils/colors/ledgerPalette'
import { formatFee } from 'src/utils/fee'
import { formatNumber, NumberFormat } from 'src/utils/number'

const EmptyLabel = 'Unassigned'

type RevenueAllocation = {
  glNumber: string | null
  revenueCents: number | null
  percentage: number
}

type GeneralLedgerBreakdownProps = {
  allocations: RevenueAllocation[]
  generalLedgerNumbers: GeneralLedgerNumberFragment[]
  totalCents: number
  timeZone?: TimeZone | null
}

type LedgerRow = {
  id: string
  glNumber: string
  description: string | null
  amountCents: number
  percentage: number
  color: string
}

type ChartDatum = DefaultRawDatum & {
  color: string
}

const GeneralLedgerBreakdown = ({
  allocations,
  generalLedgerNumbers,
  totalCents,
}: GeneralLedgerBreakdownProps) => {
  const generalLedgerMap = useMemo(
    () => new Map(generalLedgerNumbers.map((glNumber) => [glNumber.glNumber, glNumber])),
    [generalLedgerNumbers]
  )

  const tableRows = useMemo<LedgerRow[]>(() => {
    const unsortedRows = allocations.map((allocation, index) => {
      const glNumber = allocation.glNumber || `${EmptyLabel}`
      const details = allocation.glNumber ? generalLedgerMap.get(allocation.glNumber) : undefined
      const amountCents = allocation.revenueCents ?? 0
      const percentage = allocation.percentage

      return {
        id: `${glNumber}-${index}`,
        glNumber,
        description: details?.description ?? details?.glAccount ?? null,
        amountCents,
        percentage,
        color: getLedgerColor(index),
      }
    })

    const sortedRows = unsortedRows.sort((left, right) => right.amountCents - left.amountCents)

    const rows = sortedRows.map((row, index) => ({
      ...row,
      color: getLedgerColor(index),
    }))
    rows.push({
      id: 'total',
      glNumber: 'Total',
      description: '',
      amountCents: totalCents,
      percentage: 100,
      color: '',
    })
    return rows
  }, [allocations, generalLedgerMap, totalCents])

  const columnHelper = useMemo(() => createColumnHelper<LedgerRow>(), [])

  const columns = useMemo(() => {
    return [
      columnHelper.accessor((row) => row.glNumber, {
        id: 'glNumber',
        header: 'GL code',
        cell: ({ row: { original } }) => (
          <HStack gap={2} align="center">
            <span
              className={twMerge('h-3 w-3 rounded-full')}
              style={{ backgroundColor: original.color }}
            />
            <VStack gap={0} align="start">
              <T.Body className="font-medium text-neutral-900">{original.glNumber}</T.Body>
              {original.description ? (
                <T.BodySmall className="text-gray-500">{original.description}</T.BodySmall>
              ) : null}
            </VStack>
          </HStack>
        ),
        enableSorting: false,
      }),
      columnHelper.accessor((row) => row.percentage, {
        id: 'percentage',
        header: 'Percentage',
        cell: (info) => (
          <T.Body>{formatNumber(info.getValue(), { format: NumberFormat.Percentage })}</T.Body>
        ),
        enableSorting: false,
      }),
      columnHelper.accessor((row) => row.amountCents, {
        id: 'amountCents',
        header: 'Amount',
        cell: (info) => (
          <T.Body className="font-medium text-neutral-900">{formatFee(info.getValue())}</T.Body>
        ),
        enableSorting: false,
      }),
    ]
  }, [columnHelper])

  const chartData = useMemo<ChartDatum[]>(() => {
    return tableRows
      .filter((row) => row.amountCents > 0)
      .filter((row) => row.id !== 'total')
      .map((row) => ({
        id: `GL #${row.glNumber}`,
        label: `GL #${row.glNumber}`,
        value: row.percentage,
        color: row.color,
      }))
  }, [tableRows])

  const colors = useMemo(() => {
    return Array.from(new Set(tableRows.map((row) => row.color)))
  }, [tableRows])

  const formatValue = useCallback((value?: number | null) => {
    return formatNumber(value ?? 0, { format: NumberFormat.Percentage, maximumFractionDigits: 1 })
  }, [])

  const hasAllocations = tableRows.length > 0
  const getRowId = useCallback((row: LedgerRow) => row.id, [])

  return (
    <VStack gap={4}>
      <HStack justify="space-between" align="center">
        <T.H3 className="font-semibold">General Ledger Breakdown</T.H3>
      </HStack>
      {!hasAllocations ? (
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <T.Body>No revenue allocations were provided for this settlement.</T.Body>
        </div>
      ) : (
        <HStack gap={2} align="start" wrap="wrap">
          <div className="flex-1">
            <Table
              columns={columns}
              data={tableRows}
              enablePagination={false}
              shouldHideHeader={false}
              getRowId={getRowId}
            />
          </div>
          <div className="min-w-[400px] flex-1">
            <PieChart
              data={chartData}
              innerRadius={0}
              padAngle={0}
              colors={colors}
              formatValue={formatValue}
              isCenterTextHidden
            />
          </div>
        </HStack>
      )}
    </VStack>
  )
}

export default React.memo(GeneralLedgerBreakdown)
